import { useState, type FormEvent, type ReactNode } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/lib/api-client";
import {
  addAdminProductMedia,
  getAdminBrands,
  getAdminCategories,
  getAdminProductDetail,
  removeAdminProductMedia,
  setAdminProductMediaPrimary,
  updateAdminProduct,
  uploadAdminProductMedia,
  type AdminProductStatus,
} from "@/lib/admin-api-client";
import { flattenCategoryTree } from "@/components/admin/adminCategoryTree";
import { describeAdminError } from "@/components/admin/adminErrors";

/** Limites d'envoi — identiques à `MAX_IMAGE_BYTES`/`MAX_VIDEO_BYTES` côté API. */
const MAX_IMAGE_MB = 2;
const MAX_VIDEO_MB = 50;

const INPUT_CLASS = "w-full rounded-sm border border-line px-3 py-2 text-sm text-ink";

/**
 * `/admin/catalogue/produits/:id` — équivalent de
 * apps/web/app/(admin)/admin/catalogue/produits/[id]/page.tsx : édition du
 * produit + de sa variante par défaut (prix, statut, + champs élargis ci-dessous),
 * tags, images et SEO court (`metaTitle`/`metaDescription`). Pas d'éditeur
 * multi-variantes à ce lot : la liste "Variantes" ci-dessous ne montre donc jamais
 * qu'une seule ligne en pratique (inchangé de l'original). Le stock affiché est en
 * LECTURE SEULE (lien vers `/admin/stock`, seul chemin d'écriture audité — cf.
 * `AdminInventoryService`) : cette fiche n'écrit jamais `Inventory`.
 *
 * `notFound()` (Next.js) devient un état local dérivé de `ApiError.status`
 * sur la requête détail. Le bandeau de succès affiché juste après création
 * (`?success=1` côté original) est ici lu depuis `location.state.success`
 * (posé par `AdminCatalogueProduitNouveauPage` au moment de la navigation).
 *
 * Un seul bandeau `feedback` partagé par TOUTES les mutations de la page (édition
 * du formulaire principal ET actions sur les images) — même convention que
 * `AdminStockPage` (filtre + ajustement partagent aussi un seul bandeau).
 */
export function AdminCatalogueProduitDetailPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [feedback, setFeedback] = useState<{ type: "error" | "success"; message: string } | null>(
    (location.state as { success?: boolean } | null)?.success
      ? { type: "success", message: "Produit enregistré." }
      : null,
  );

  const productQuery = useQuery({
    queryKey: ["admin", "product", id],
    queryFn: () => getAdminProductDetail(undefined, id as string),
    enabled: Boolean(id),
    retry: false,
  });
  const brandsQuery = useQuery({ queryKey: ["admin", "brands"], queryFn: () => getAdminBrands() });
  const categoriesQuery = useQuery({ queryKey: ["admin", "categories"], queryFn: () => getAdminCategories() });

  const updateMutation = useMutation({
    mutationFn: (input: Parameters<typeof updateAdminProduct>[2]) =>
      updateAdminProduct(undefined, id as string, input),
    onSuccess: () => {
      setFeedback({ type: "success", message: "Produit enregistré." });
      queryClient.invalidateQueries({ queryKey: ["admin", "product", id] });
      queryClient.invalidateQueries({ queryKey: ["admin", "products"] });
    },
    onError: (err) => setFeedback({ type: "error", message: describeAdminError(err) }),
  });

  const addImageMutation = useMutation({
    mutationFn: ({ url, kind }: { url: string; kind: "image" | "video" }) =>
      addAdminProductMedia(undefined, id as string, url, kind),
    onSuccess: (_data, { kind }) => {
      setFeedback({ type: "success", message: kind === "video" ? "Vidéo ajoutée." : "Image ajoutée." });
      queryClient.invalidateQueries({ queryKey: ["admin", "product", id] });
    },
    onError: (err) => setFeedback({ type: "error", message: describeAdminError(err) }),
  });

  const uploadImageMutation = useMutation({
    mutationFn: (file: File) => uploadAdminProductMedia(undefined, id as string, file),
    onSuccess: (_data, file) => {
      setFeedback({ type: "success", message: file.type.startsWith("video/") ? "Vidéo envoyée." : "Image envoyée." });
      queryClient.invalidateQueries({ queryKey: ["admin", "product", id] });
    },
    onError: (err) => setFeedback({ type: "error", message: describeAdminError(err) }),
  });

  const removeImageMutation = useMutation({
    mutationFn: (mediaId: string) => removeAdminProductMedia(undefined, id as string, mediaId),
    onSuccess: () => {
      setFeedback({ type: "success", message: "Média supprimé." });
      queryClient.invalidateQueries({ queryKey: ["admin", "product", id] });
    },
    onError: (err) => setFeedback({ type: "error", message: describeAdminError(err) }),
  });

  const setPrimaryImageMutation = useMutation({
    mutationFn: (mediaId: string) => setAdminProductMediaPrimary(undefined, id as string, mediaId),
    onSuccess: () => {
      setFeedback({ type: "success", message: "Image primaire mise à jour." });
      queryClient.invalidateQueries({ queryKey: ["admin", "product", id] });
    },
    onError: (err) => setFeedback({ type: "error", message: describeAdminError(err) }),
  });

  function parseNonNegativeInt(raw: string): number | null {
    if (!raw) return null;
    const value = Number.parseInt(raw, 10);
    if (!Number.isInteger(value) || value < 0 || String(value) !== raw) return null;
    return value;
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFeedback(null);
    const formData = new FormData(event.currentTarget);

    // Parse un champ entier positif optionnel (prix, poids…) ; pose le bandeau d'erreur
    // et signale l'échec via `hasError` (capturé par closure) plutôt que de lever — même
    // esprit que la validation `priceMinor` d'origine, juste factorisé pour les 4 champs
    // numériques optionnels du lot (prix, prix barré, prix de revient, poids).
    let hasError = false;
    function parseOptionalPositiveInt(name: string, label: string): number | undefined {
      const raw = String(formData.get(name) ?? "").trim();
      if (!raw) return undefined;
      const parsed = parseNonNegativeInt(raw);
      if (parsed === null) {
        setFeedback({ type: "error", message: `${label} doit être un entier positif.` });
        hasError = true;
        return undefined;
      }
      return parsed;
    }

    const priceMinor = parseOptionalPositiveInt("priceMinor", "Le prix (FCFA)");
    if (hasError) return;
    const compareAtPriceMinor = parseOptionalPositiveInt("compareAtPriceMinor", "Le prix barré (FCFA)");
    if (hasError) return;
    const costPriceMinor = parseOptionalPositiveInt("costPriceMinor", "Le prix de revient (FCFA)");
    if (hasError) return;
    const weightG = parseOptionalPositiveInt("weightG", "Le poids (g)");
    if (hasError) return;

    updateMutation.mutate({
      name: String(formData.get("name") ?? "").trim() || undefined,
      subtitle: String(formData.get("subtitle") ?? "").trim() || undefined,
      nameInternational: String(formData.get("nameInternational") ?? "").trim() || undefined,
      descriptionShort: String(formData.get("descriptionShort") ?? "").trim() || undefined,
      descriptionLong: String(formData.get("descriptionLong") ?? "").trim() || undefined,
      usageInstructions: String(formData.get("usageInstructions") ?? "").trim() || undefined,
      precautions: String(formData.get("precautions") ?? "").trim() || undefined,
      // Case à cocher : toujours envoyée (jamais `undefined`), contrairement aux champs
      // texte ci-dessus — sinon décocher la case n'aurait jamais d'effet côté API.
      ageRestricted: formData.get("ageRestricted") === "on",
      status: (String(formData.get("status") ?? "").trim() || undefined) as AdminProductStatus | undefined,
      brandId: String(formData.get("brandId") ?? "").trim() || undefined,
      primaryCategoryId: String(formData.get("primaryCategoryId") ?? "").trim() || undefined,
      // Idem : toujours envoyé, même vide — représente l'état COMPLET des tags du
      // produit (cf. `UpdateAdminProductInput.tags`), pas un simple ajout.
      tags: String(formData.get("tags") ?? "").trim(),
      priceMinor,
      compareAtPriceMinor,
      costPriceMinor,
      weightG,
      barcode: String(formData.get("barcode") ?? "").trim() || undefined,
      metaTitle: String(formData.get("metaTitle") ?? "").trim() || undefined,
      metaDescription: String(formData.get("metaDescription") ?? "").trim() || undefined,
    });
  }

  function handleAddImageSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFeedback(null);
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);
    const url = String(formData.get("imageUrl") ?? "").trim();
    const kind = formData.get("mediaKind") === "video" ? "video" : "image";
    if (!url) {
      setFeedback({ type: "error", message: "L'URL du média est obligatoire." });
      return;
    }
    addImageMutation.mutate({ url, kind }, { onSuccess: () => formEl.reset() });
  }

  function handleUploadImageSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFeedback(null);
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);
    const file = formData.get("imageFile");
    if (!(file instanceof File) || file.size === 0) {
      setFeedback({ type: "error", message: "Choisissez un fichier image ou vidéo à envoyer." });
      return;
    }
    // Mêmes limites que l'API (`MAX_IMAGE_BYTES`/`MAX_VIDEO_BYTES`) : refus immédiat plutôt
    // qu'après plusieurs minutes d'envoi d'une vidéo trop lourde.
    const isVideo = file.type.startsWith("video/");
    const maxMb = isVideo ? MAX_VIDEO_MB : MAX_IMAGE_MB;
    if (file.size > maxMb * 1024 * 1024) {
      setFeedback({
        type: "error",
        message: `Fichier trop lourd : ${isVideo ? "vidéo" : "image"} de ${maxMb} Mo maximum.`,
      });
      return;
    }
    uploadImageMutation.mutate(file, { onSuccess: () => formEl.reset() });
  }

  if (productQuery.isLoading) {
    return <p className="text-sm text-muted">Chargement…</p>;
  }

  if (productQuery.isError) {
    const notFound = productQuery.error instanceof ApiError && productQuery.error.status === 404;
    return (
      <div>
        <Link to="/admin/catalogue/produits" className="text-sm font-medium text-navy hover:underline">
          ← Produits
        </Link>
        <p className="mt-4 text-sm text-red-700">
          {notFound ? "Produit introuvable." : describeAdminError(productQuery.error)}
        </p>
      </div>
    );
  }

  const product = productQuery.data;
  if (!product) return null;

  const brands = brandsQuery.data ?? [];
  const flatCategories = flattenCategoryTree(categoriesQuery.data ?? []);
  const defaultVariant = product.variants.find((v) => v.isDefault) ?? product.variants[0];

  return (
    <div>
      <div className="flex items-center gap-3">
        <Link to="/admin/catalogue/produits" className="text-sm font-medium text-navy hover:underline">
          ← Produits
        </Link>
      </div>

      <h1 className="mt-2 font-display text-2xl font-semibold text-ink">{product.name}</h1>
      <p className="mt-1 text-sm text-muted">
        SKU racine <span className="font-mono">{product.skuRoot}</span> · slug{" "}
        <span className="font-mono">{product.slug}</span>
      </p>

      {feedback && (
        <p
          className={[
            "mt-4 rounded-sm border px-4 py-3 text-sm",
            feedback.type === "error"
              ? "border-red-300 bg-red-50 text-red-700"
              : "border-green-300 bg-green-50 text-green-700",
          ].join(" ")}
        >
          {feedback.message}
        </p>
      )}

      <form onSubmit={handleSubmit} className="mt-6 max-w-2xl space-y-5 rounded-sm border border-line bg-white p-6">
        <Field label="Nom" htmlFor="name">
          <input id="name" name="name" type="text" defaultValue={product.name} className={INPUT_CLASS} />
        </Field>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Field label="Sous-titre" htmlFor="subtitle">
            <input
              id="subtitle"
              name="subtitle"
              type="text"
              defaultValue={product.subtitle ?? ""}
              className={INPUT_CLASS}
            />
          </Field>

          <Field label="Nom international" htmlFor="nameInternational">
            <input
              id="nameInternational"
              name="nameInternational"
              type="text"
              defaultValue={product.nameInternational ?? ""}
              className={INPUT_CLASS}
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Field label="Statut" htmlFor="status">
            <select id="status" name="status" defaultValue={product.status} className={INPUT_CLASS}>
              <option value="DRAFT">Brouillon</option>
              <option value="ACTIVE">Actif</option>
              <option value="ARCHIVED">Archivé</option>
            </select>
          </Field>

          <Field
            label={`Prix de la variante par défaut (FCFA)${defaultVariant ? ` — SKU ${defaultVariant.sku}` : ""}`}
            htmlFor="priceMinor"
          >
            <input
              id="priceMinor"
              name="priceMinor"
              type="number"
              min={0}
              step={1}
              defaultValue={defaultVariant?.price.amountMinor}
              className={INPUT_CLASS}
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Field label="Prix barré (FCFA, avant réduction)" htmlFor="compareAtPriceMinor">
            <input
              id="compareAtPriceMinor"
              name="compareAtPriceMinor"
              type="number"
              min={0}
              step={1}
              defaultValue={defaultVariant?.compareAtPrice?.amountMinor}
              className={INPUT_CLASS}
            />
          </Field>

          <Field label="Prix de revient (FCFA)" htmlFor="costPriceMinor">
            <input
              id="costPriceMinor"
              name="costPriceMinor"
              type="number"
              min={0}
              step={1}
              defaultValue={defaultVariant?.costPrice?.amountMinor}
              className={INPUT_CLASS}
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Field label="Poids (g)" htmlFor="weightG">
            <input
              id="weightG"
              name="weightG"
              type="number"
              min={0}
              step={1}
              defaultValue={defaultVariant?.weightG ?? undefined}
              className={INPUT_CLASS}
            />
          </Field>

          <Field label="Code-barres" htmlFor="barcode">
            <input
              id="barcode"
              name="barcode"
              type="text"
              defaultValue={defaultVariant?.barcode ?? ""}
              className={INPUT_CLASS}
            />
          </Field>
        </div>

        <div className="rounded-sm border border-line bg-cream/40 px-4 py-3 text-sm text-ink">
          <p className="font-medium">Stock (variante par défaut) — lecture seule</p>
          {defaultVariant?.stock ? (
            <p className="mt-1 text-muted">
              {defaultVariant.stock.quantityOnHand} en stock · {defaultVariant.stock.quantityAvailable} disponible(s)
            </p>
          ) : (
            <p className="mt-1 text-muted">Aucune ligne de stock pour cette variante.</p>
          )}
          <Link
            to={`/admin/stock?search=${encodeURIComponent(product.skuRoot)}`}
            className="mt-1 inline-block text-sm font-medium text-navy hover:underline"
          >
            Gérer le stock →
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Field label="Marque" htmlFor="brandId">
            <select id="brandId" name="brandId" defaultValue={product.brand?.id ?? ""} className={INPUT_CLASS}>
              <option value="">— Aucune —</option>
              {brands.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Catégorie principale" htmlFor="primaryCategoryId">
            <select
              id="primaryCategoryId"
              name="primaryCategoryId"
              defaultValue={product.primaryCategory?.id ?? ""}
              className={INPUT_CLASS}
            >
              <option value="">— Aucune —</option>
              {flatCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {"— ".repeat(cat.level)}
                  {cat.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <p className="-mt-3 text-xs text-muted">
          Laisser sur « — Aucune — » ne modifie pas la valeur actuelle (pas de dissociation possible à ce lot).
        </p>

        <Field label="Tags" htmlFor="tags">
          <input
            id="tags"
            name="tags"
            type="text"
            defaultValue={product.tags.map((tag) => tag.label).join(", ")}
            placeholder="ex. korean-skincare, vegan"
            className={INPUT_CLASS}
          />
        </Field>
        <p className="-mt-3 text-xs text-muted">
          Séparés par une virgule ou un pipe (|) — remplace l'intégralité des tags du produit (laisser vide retire
          tous les tags).
        </p>

        <label className="flex items-center gap-2 text-sm text-ink">
          <input type="checkbox" name="ageRestricted" defaultChecked={product.ageRestricted} />
          Produit à âge restreint (vente interdite aux mineurs)
        </label>

        <Field label="Description courte" htmlFor="descriptionShort">
          <textarea
            id="descriptionShort"
            name="descriptionShort"
            rows={2}
            defaultValue={product.descriptionShort ?? ""}
            className={INPUT_CLASS}
          />
        </Field>

        <Field label="Description longue" htmlFor="descriptionLong">
          <textarea
            id="descriptionLong"
            name="descriptionLong"
            rows={6}
            defaultValue={product.descriptionLong ?? ""}
            className={INPUT_CLASS}
          />
        </Field>

        <Field label="Instructions d'utilisation" htmlFor="usageInstructions">
          <textarea
            id="usageInstructions"
            name="usageInstructions"
            rows={3}
            defaultValue={product.usageInstructions ?? ""}
            className={INPUT_CLASS}
          />
        </Field>

        <Field label="Précautions" htmlFor="precautions">
          <textarea
            id="precautions"
            name="precautions"
            rows={3}
            defaultValue={product.precautions ?? ""}
            className={INPUT_CLASS}
          />
        </Field>

        <div className="border-t border-line pt-5">
          <h2 className="font-display text-base font-semibold text-ink">Référencement (SEO)</h2>
          <p className="mt-1 text-xs text-muted">
            Laisser vide pour utiliser le titre/la description par défaut de la fiche produit.
          </p>
          <div className="mt-3 space-y-5">
            <Field label="Titre méta" htmlFor="metaTitle">
              <input
                id="metaTitle"
                name="metaTitle"
                type="text"
                defaultValue={product.seo.metaTitle ?? ""}
                className={INPUT_CLASS}
              />
            </Field>

            <Field label="Description méta" htmlFor="metaDescription">
              <textarea
                id="metaDescription"
                name="metaDescription"
                rows={2}
                defaultValue={product.seo.metaDescription ?? ""}
                className={INPUT_CLASS}
              />
            </Field>
          </div>
        </div>

        <button
          type="submit"
          disabled={updateMutation.isPending}
          className="rounded-sm bg-navy px-5 py-2.5 text-sm font-medium text-cream hover:bg-navy/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {updateMutation.isPending ? "Enregistrement…" : "Enregistrer"}
        </button>
      </form>

      <div className="mt-6 max-w-2xl rounded-sm border border-line bg-white p-6">
        <h2 className="font-display text-lg font-semibold text-ink">Images et vidéos</h2>
        <p className="mt-1 text-xs text-muted">
          Galerie du produit — l'image entourée est l'image primaire (utilisée en priorité côté boutique). Les
          vidéos apparaissent dans la galerie de la fiche produit avec les images, et se lancent automatiquement
          (sans le son) quand le visiteur les regarde. Une vidéo ne peut pas être primaire. Pas de
          réordonnancement par glisser-déposer à ce lot.
        </p>

        {product.media.length === 0 ? (
          <p className="mt-4 text-sm text-muted">Aucune image ni vidéo pour ce produit.</p>
        ) : (
          <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4">
            {product.media.map((media) => (
              <div
                key={media.id}
                className={[
                  "relative overflow-hidden rounded-sm border",
                  media.isPrimary ? "border-navy ring-2 ring-navy" : "border-line",
                ].join(" ")}
              >
                {media.type === "VIDEO" ? (
                  <>
                    <video
                      src={media.url}
                      muted
                      playsInline
                      preload="metadata"
                      className="aspect-square w-full bg-ink object-cover"
                    />
                    <span className="absolute left-1 top-1 rounded-sm bg-navy px-1.5 py-0.5 text-[10px] font-semibold uppercase text-cream">
                      ▶ Vidéo
                    </span>
                  </>
                ) : (
                  <img src={media.url} alt={media.alt ?? ""} className="aspect-square w-full object-cover" />
                )}
                <button
                  type="button"
                  onClick={() => removeImageMutation.mutate(media.id)}
                  disabled={removeImageMutation.isPending}
                  title={media.type === "VIDEO" ? "Supprimer la vidéo" : "Supprimer l'image"}
                  aria-label={media.type === "VIDEO" ? "Supprimer la vidéo" : "Supprimer l'image"}
                  className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-xs font-bold text-red-700 hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  ×
                </button>
                {media.type !== "VIDEO" ? (
                  <button
                    type="button"
                    onClick={() => setPrimaryImageMutation.mutate(media.id)}
                    disabled={media.isPrimary || setPrimaryImageMutation.isPending}
                    className="absolute inset-x-1 bottom-1 rounded-sm bg-white/90 px-1 py-0.5 text-[10px] font-medium text-ink hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {media.isPrimary ? "Primaire" : "Définir primaire"}
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        )}

        <form onSubmit={handleAddImageSubmit} className="mt-4 flex flex-wrap items-end gap-2">
          <div className="flex flex-1 flex-col gap-1">
            <label htmlFor="imageUrl" className="text-xs font-medium text-muted">
              Ajouter une image ou une vidéo (URL)
            </label>
            <input id="imageUrl" name="imageUrl" type="text" placeholder="https://…" className={INPUT_CLASS} />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="mediaKind" className="text-xs font-medium text-muted">
              Type
            </label>
            <select id="mediaKind" name="mediaKind" defaultValue="image" className={INPUT_CLASS}>
              <option value="image">Image</option>
              <option value="video">Vidéo</option>
            </select>
          </div>
          <button
            type="submit"
            disabled={addImageMutation.isPending}
            className="rounded-sm border border-navy px-4 py-2 text-sm font-medium text-navy hover:bg-navy hover:text-cream disabled:cursor-not-allowed disabled:opacity-50"
          >
            {addImageMutation.isPending ? "Ajout…" : "Ajouter"}
          </button>
        </form>

        <form onSubmit={handleUploadImageSubmit} className="mt-3 flex flex-wrap items-end gap-2">
          <div className="flex flex-1 flex-col gap-1">
            <label htmlFor="imageFile" className="text-xs font-medium text-muted">
              Ou envoyer un fichier : image ({MAX_IMAGE_MB} Mo max) ou vidéo MP4/WebM/MOV ({MAX_VIDEO_MB} Mo max)
            </label>
            <input
              id="imageFile"
              name="imageFile"
              type="file"
              accept="image/*,video/mp4,video/webm,video/quicktime"
              className="w-full rounded-sm border border-line px-3 py-1.5 text-sm text-ink"
            />
          </div>
          <button
            type="submit"
            disabled={uploadImageMutation.isPending}
            className="rounded-sm border border-navy px-4 py-2 text-sm font-medium text-navy hover:bg-navy hover:text-cream disabled:cursor-not-allowed disabled:opacity-50"
          >
            {uploadImageMutation.isPending ? "Envoi…" : "Envoyer"}
          </button>
        </form>
      </div>

      <div className="mt-6 max-w-2xl rounded-sm border border-line bg-white p-6">
        <h2 className="font-display text-lg font-semibold text-ink">Variantes</h2>
        <p className="mt-1 text-xs text-muted">
          Un seul éditeur de variante « par défaut » à ce lot — pas de gestion multi-variantes
          (couleur, taille…) depuis le back-office pour le moment.
        </p>
        <table className="mt-3 w-full text-left text-sm">
          <thead>
            <tr className="border-b border-line text-xs uppercase tracking-wide text-muted">
              <th className="py-2">SKU</th>
              <th className="py-2">Par défaut</th>
              <th className="py-2">Statut</th>
              <th className="py-2 text-right">Prix</th>
            </tr>
          </thead>
          <tbody>
            {product.variants.map((v) => (
              <tr key={v.id} className="border-b border-line last:border-0">
                <td className="py-2 font-mono text-xs text-muted">{v.sku}</td>
                <td className="py-2 text-ink">{v.isDefault ? "Oui" : "Non"}</td>
                <td className="py-2 text-ink">{v.status === "ACTIVE" ? "Active" : "Discontinuée"}</td>
                <td className="py-2 text-right text-ink">{v.price.formatted}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, htmlFor, children }: { label: string; htmlFor: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={htmlFor} className="text-sm font-medium text-ink">
        {label}
      </label>
      {children}
    </div>
  );
}
